import { gql } from '@apollo/client';

// Client documents of the check-in / check-out / dispute feature. They only use fields of the server contract
// (src/data/types/InspectionType.js). Statuses are strings ('success', 'notLoggedIn', 'failed').

const photoFields = `
  id
  kind
  position
  uploadedBy
  createdAt
  url
  thumbUrl
`;

const inspectionFields = `
  id
  reservationId
  type
  status
  version
  authorId
  authorRole
  generalState
  mileage
  fuelLevel
  keysConfirmed
  hasDamage
  damageDescription
  comment
  checklist {
    key
    present
  }
  hostValidatedAt
  guestValidatedAt
  contestedAt
  lockedAt
  createdAt
  updatedAt
  photos {
    ${photoFields}
  }
  viewerRole
  windowOpen
  windowReason
  canEdit
  canValidate
  canContest
`;

const inspectionResultFields = `
  status
  errorMessage
  windowOpen
  windowReason
  opensAt
  closesAt
  viewerRole
  inspection {
    ${inspectionFields}
  }
`;

const disputeFields = `
  id
  reservationId
  openedBy
  openedByRole
  reasonCode
  status
  isActive
  depositAmount
  depositStatus
  createdAt
  closedAt
  statements {
    id
    disputeId
    userId
    role
    content
    attachments
    isInternal
    createdAt
  }
  decisions {
    id
    disputeId
    adminId
    type
    retainedAmount
    refundAmount
    requestedAmount
    currency
    motive
    attachments
    createdAt
  }
`;

const disputeResultFields = `
  status
  errorMessage
  dispute {
    ${disputeFields}
  }
`;

// Reservation header shared by the check-in and check-out pages (vehicle, dates, both parties, summary fields).
export const getInspectionReservationQuery = gql`
  query getInspectionReservation($reservationId: Int!) {
    getItinerary(reservationId: $reservationId) {
      id
      listId
      hostId
      guestId
      checkIn
      checkOut
      startTime
      endTime
      listTitle
      dynamicListTitle
      reservationState
      paymentState
      currency
      securityDeposit
      confirmationCode
      checkinStatus
      checkoutStatus
      hasDamage
      hasDispute
      globalStatus
      inspectionEnabled
      listData {
        id
        title
        street
        city
        state
        country
        coverPhoto
        transmission
        listPhotos {
          id
          name
        }
      }
      hostData {
        profileId
        firstName
        picture
      }
      guestData {
        profileId
        firstName
        picture
      }
    }
  }
`;

export const getInspectionQuery = gql`
  query getInspection($reservationId: Int!, $type: String!) {
    getInspection(reservationId: $reservationId, type: $type) {
      ${inspectionResultFields}
    }
  }
`;

export const getChecklistQuery = gql`
  query getChecklist($reservationId: Int!) {
    getChecklist(reservationId: $reservationId) {
      status
      errorMessage
      results {
        key
        labelKey
        required
        sortOrder
      }
    }
  }
`;

export const getDisputeQuery = gql`
  query getDispute($reservationId: Int!) {
    getDispute(reservationId: $reservationId) {
      ${disputeResultFields}
    }
  }
`;

export const getInspectionComparisonQuery = gql`
  query getInspectionComparison($reservationId: Int!) {
    getInspectionComparison(reservationId: $reservationId) {
      status
      errorMessage
      kmDriven
      fuelDelta
      hasDamage
      newDamagePhotos
      globalStatus
      alerts {
        code
        severity
        value
      }
      checkin {
        ${inspectionFields}
      }
      checkout {
        ${inspectionFields}
      }
    }
  }
`;

export const saveInspectionDraftMutation = gql`
  mutation saveInspectionDraft($reservationId: Int!, $type: String!, $input: InspectionInput!, $expectedVersion: Int) {
    saveInspectionDraft(reservationId: $reservationId, type: $type, input: $input, expectedVersion: $expectedVersion) {
      ${inspectionResultFields}
    }
  }
`;

export const validateInspectionMutation = gql`
  mutation validateInspection($reservationId: Int!, $type: String!, $version: Int!) {
    validateInspection(reservationId: $reservationId, type: $type, version: $version) {
      ${inspectionResultFields}
    }
  }
`;

export const contestInspectionMutation = gql`
  mutation contestInspection($reservationId: Int!, $type: String!, $comment: String!) {
    contestInspection(reservationId: $reservationId, type: $type, comment: $comment) {
      ${inspectionResultFields}
    }
  }
`;

export const submitCheckoutMutation = gql`
  mutation submitCheckout($reservationId: Int!, $hasDamage: Boolean!, $input: CheckoutInput!) {
    submitCheckout(reservationId: $reservationId, hasDamage: $hasDamage, input: $input) {
      ${inspectionResultFields}
    }
  }
`;

export const openDisputeMutation = gql`
  mutation openDispute($reservationId: Int!, $reasonCode: String!, $comment: String!) {
    openDispute(reservationId: $reservationId, reasonCode: $reasonCode, comment: $comment) {
      ${disputeResultFields}
    }
  }
`;

export const addDisputeStatementMutation = gql`
  mutation addDisputeStatement($disputeId: Int!, $content: String!, $attachments: [Int!]) {
    addDisputeStatement(disputeId: $disputeId, content: $content, attachments: $attachments) {
      ${disputeResultFields}
    }
  }
`;

// Existing review module (R11): a separate call, its failure never blocks the check-out.
export const writeReviewMutation = gql`
  mutation writeReview($reservationId: Int!, $listId: Int!, $receiverId: String!, $reviewContent: String!, $rating: Float!) {
    writeReview(reservationId: $reservationId, listId: $listId, receiverId: $receiverId, reviewContent: $reviewContent, rating: $rating) {
      status
    }
  }
`;

// ---------- admin ----------

export const getInspectionSettingsQuery = gql`
  query getInspectionSettings {
    getInspectionSettings {
      status
      errorMessage
      inspectionEnabled
      checkinOpensHoursBefore
      checkoutWindowDays
      inspectionGraceHours
      photoMax
      photoMin
      disputeWindowDays
      timeZone
    }
  }
`;

export const setInspectionSettingsMutation = gql`
  mutation setInspectionSettings(
    $inspectionEnabled: Boolean!, $checkinOpensHoursBefore: Int!, $checkoutWindowDays: Int!, $inspectionGraceHours: Int!,
    $photoMax: Int!, $photoMin: Int!, $disputeWindowDays: Int!
  ) {
    setInspectionSettings(
      inspectionEnabled: $inspectionEnabled, checkinOpensHoursBefore: $checkinOpensHoursBefore, checkoutWindowDays: $checkoutWindowDays,
      inspectionGraceHours: $inspectionGraceHours, photoMax: $photoMax, photoMin: $photoMin, disputeWindowDays: $disputeWindowDays
    ) {
      status
      errorMessage
      inspectionEnabled
      checkinOpensHoursBefore
      checkoutWindowDays
      inspectionGraceHours
      photoMax
      photoMin
      disputeWindowDays
      timeZone
    }
  }
`;

export const decideDisputeMutation = gql`
  mutation decideDispute($disputeId: Int!, $type: String!, $retainedAmount: Float, $requestedAmount: Float, $motive: String!, $attachments: [Int!]) {
    decideDispute(disputeId: $disputeId, type: $type, retainedAmount: $retainedAmount, requestedAmount: $requestedAmount, motive: $motive, attachments: $attachments) {
      status
      errorMessage
      decisionId
      retainedAmount
      refundAmount
      requestedAmount
      dispute {
        id
        reservationId
        status
        isActive
        closedAt
      }
    }
  }
`;

export const updateDisputeStatusMutation = gql`
  mutation updateDisputeStatus($disputeId: Int!, $status: String!) {
    updateDisputeStatus(disputeId: $disputeId, status: $status) {
      status
      errorMessage
      dispute {
        id
        reservationId
        status
        isActive
        closedAt
      }
    }
  }
`;

// Admin detail page: the reservation header plus the check-in / check-out summary of the list.
export const getAdminInspectionReservationQuery = gql`
  query viewReservationAdminInspection($id: Int!) {
    viewReservationAdmin(id: $id) {
      id
      listId
      hostId
      guestId
      checkIn
      checkOut
      reservationState
      currency
      total
      securityDeposit
      confirmationCode
      createdAt
      claimStatus
      claimAmount
      claimPayout
      claimRefund
      checkinStatus
      checkoutStatus
      hasDamage
      hasDispute
      globalStatus
      closureState
      listData {
        id
        title
      }
      hostData {
        profileId
        firstName
      }
      guestData {
        profileId
        firstName
      }
    }
  }
`;
